import type { PostMetricAverages, PublishedPostDto } from "@nlc/shared-types";

import { ArrowDown, ArrowUp } from "lucide-react";
import { twMerge } from "tailwind-merge";

import { SOCIAL_PLATFORM_LABELS } from "../../socialPlatformLabels";
import { formatMetric } from "../formatMetric";
import { POST_FORMAT_LABELS } from "../postFormatLabels";
import { engagementRate } from "./engagementRate";
import { percentDifference } from "./percentDifference";

const percentFormat = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 1,
});

export function PostPerformance({
  averagePostCount,
  averages,
  post,
}: {
  averagePostCount: number;
  averages: PostMetricAverages;
  post: PublishedPostDto;
}) {
  const { insights } = post;
  const postRate = engagementRate(insights?.totalInteractions, insights?.reach);
  const averageRate = engagementRate(
    averages.totalInteractions,
    averages.reach,
  );

  return (
    <section className="flex flex-col gap-2xl rounded-2xl border border-border-secondary bg-bg-primary p-2xl">
      <div className="flex flex-col gap-md">
        <span className="text-sm font-semibold text-text-brand-secondary">
          How this post did
        </span>
        <h2 className="text-display-xs font-semibold text-text-primary">
          {headline(insights?.views, averages.views)}
        </h2>
        <p className="text-sm text-text-tertiary">
          {comparisonBasis(post, averagePostCount)}
        </p>
        {insights?.syncError && (
          <p className="text-sm text-text-warning-primary">
            {insights.syncError}
          </p>
        )}
      </div>
      <dl className="grid gap-lg sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          average={averages.views}
          description="Times this post appeared on screen"
          label="Views"
          value={insights?.views}
        />
        <StatCard
          average={averages.reach}
          description="Accounts that saw this post"
          label="People reached"
          value={insights?.reach}
        />
        <StatCard
          average={averages.totalInteractions}
          description="Likes, comments, shares and saves"
          label="Interactions"
          value={insights?.totalInteractions}
        />
        <StatCard
          average={averageRate}
          description="Interactions per person reached"
          format={(rate) => `${percentFormat.format(rate)}%`}
          label="Engagement rate"
          value={postRate}
        />
      </dl>
    </section>
  );
}

function headline(
  views: null | number | undefined,
  averageViews: null | number,
): string {
  if (views === null || views === undefined)
    return "We're still collecting the numbers for this post";

  const seen = `This post was seen ${formatMetric(views)} ${views === 1 ? "time" : "times"}`;
  const difference = percentDifference(views, averageViews);

  if (difference === null) return seen;
  if (difference === 0) return `${seen}, right on your average`;

  return `${seen}, ${Math.abs(difference)}% ${difference > 0 ? "more" : "less"} than your average`;
}

function comparisonBasis(post: PublishedPostDto, postCount: number): string {
  const platform = post.page
    ? ` on ${SOCIAL_PLATFORM_LABELS[post.page.platform]}`
    : "";
  const format = POST_FORMAT_LABELS[post.format].toLowerCase();
  const formats = postCount === 1 ? format : `${format}s`;

  return postCount === 0
    ? `${post.brand.name} has no other ${format}s${platform} from the last 30 days to compare with yet.`
    : `Compared with the average of ${postCount} other ${formats} from ${post.brand.name}${platform} in the last 30 days.`;
}

function StatCard({
  average,
  description,
  format = formatMetric,
  label,
  value,
}: {
  average: null | number;
  description: string;
  format?: (value: number) => string;
  label: string;
  value: null | number | undefined;
}) {
  const difference = percentDifference(value, average);

  return (
    <div className="flex flex-col gap-sm rounded-xl border border-border-secondary p-xl">
      <dt className="text-sm font-semibold text-text-secondary">{label}</dt>
      <dd className="flex flex-col gap-sm">
        <span className="text-display-sm font-semibold text-text-primary">
          {value === null || value === undefined ? "—" : format(value)}
        </span>
        {difference !== null && (
          <span
            className={twMerge(
              "flex items-center gap-xxs text-sm font-medium",
              difference >= 0
                ? "text-text-success-primary"
                : "text-text-error-primary",
            )}
          >
            {difference >= 0 ? (
              <ArrowUp className="size-4" />
            ) : (
              <ArrowDown className="size-4" />
            )}
            {Math.abs(difference)}% {difference >= 0 ? "above" : "below"}{" "}
            average
          </span>
        )}
        <span className="text-sm text-text-tertiary">{description}</span>
      </dd>
    </div>
  );
}
